import { ApiAuthenticationFailedException } from '../errors'
import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'
import { asterAccountCategory } from './constants'

/**
 * Aster API用のクエリ文字列生成（URLエンコードなし）
 * 署名計算時はエンコード前の値を使用する必要があるため
 */
const buildQueryStringNoEncode = (params?: Record<string, unknown>): string => {
  if (!params || Object.keys(params).length === 0) return ''
  const sortedKeys = Object.keys(params).sort()
  return sortedKeys.map((key) => `${key}=${String(params[key])}`).join('&')
}

const SPOT_BASE_URL = 'https://sapi.asterdex.com'
const FUTURES_BASE_URL = 'https://fapi.asterdex.com'
const RECV_WINDOW = 5000 // 5秒の有効期間

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** リクエストメソッド（GETの場合はContent-Typeを含めない） */
  method: 'GET' | 'POST'
}): Promise<Record<string, string>> => {
  const { apiKey, createHeaders, method } = params

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  const headers: Record<string, string> = {
    'X-MBX-APIKEY': apiKey,
    ...overrideHeaders,
  }

  // POSTリクエストの場合のみContent-Typeを追加
  if (method === 'POST') {
    headers['Content-Type'] = 'application/x-www-form-urlencoded'
  }

  return headers
}

export const createClient = ({
  apiKey,
  apiSecret,
  noAuth = false,
  createHeaders,
  proxy,
}: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** 認証なしで実行する場合は noAuth を true に設定 */
  noAuth?: boolean
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      // パスに応じてベースURLを選択
      let baseUrl: string
      if (path.startsWith('/fapi')) {
        baseUrl = FUTURES_BASE_URL
      } else {
        baseUrl = SPOT_BASE_URL
      }

      if (noAuth) {
        const queryString = buildQueryString({
          ...query,
          ...body,
        })
        switch (method) {
          case 'GET': {
            return {
              url: `${baseUrl}${path}${queryString ? `?${queryString}` : ''}`,
              headers: createHeaders ? await createHeaders() : undefined,
            }
          }
          case 'POST': {
            return {
              url: `${baseUrl}${path}`,
              body: queryString,
              headers: createHeaders ? await createHeaders() : undefined,
            }
          }
          default: {
            throw new Error(`Unsupported method: ${method satisfies never}`)
          }
        }
      }

      const timestamp = Date.now().toString()
      const recvWindow = RECV_WINDOW.toString()

      // パラメータの準備
      const params = {
        ...query,
        ...body,
        recvWindow,
        timestamp,
      }

      // 署名計算用の文字列（エンコードなし）
      const dataString = buildQueryStringNoEncode(params)

      // HMAC-SHA256署名の生成
      const signature = await sign({
        algorithm: 'sha256',
        payload: dataString,
        secret: apiSecret,
        encoding: 'hex',
      })

      const headers = await createAuthHeaders({
        apiKey,
        createHeaders,
        method,
      })

      if (method === 'GET') {
        // GETの場合: query parametersにsignatureを追加
        const queryWithSignature = buildQueryString({ ...params, signature })
        const url = `${baseUrl}${path}?${queryWithSignature}`

        return {
          url,
          headers,
        }
      }
      // POSTの場合: bodyにsignatureを追加
      const bodyWithSignature = buildQueryString({ ...params, signature })
      const url = `${baseUrl}${path}`

      return {
        url,
        headers,
        body: bodyWithSignature,
      }
    },
    getDelayForRetry: ({ statusCode, errorMessage }) => {
      // 400
      if (statusCode === 400) {
        // Timestamp for this request is outside of the recvWindow / Timestamp for this request was 1000ms ahead of the server's time エラーは 1秒待機してリトライ
        if (
          errorMessage.includes(
            'Timestamp for this request is outside of the recvWindow',
          ) ||
          errorMessage.includes(
            "Timestamp for this request was 1000ms ahead of the server's time",
          )
        ) {
          return 1000
        }
        // Invalid Api-Key エラーは認証失敗エラーを投げる
        if (errorMessage.includes('Invalid Api-Key')) {
          throw new ApiAuthenticationFailedException(asterAccountCategory)
        }
      }
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        // ただし、Invalid API-key, IP, or permissions for action エラーの場合はリトライしない
        if (
          errorMessage.includes(
            'Invalid API-key, IP, or permissions for action',
          )
        ) {
          return undefined
        }
        return 1000
      }
      // 418 エラーはIPがブロックされている（リトライしない）
      if (statusCode === 418) {
        return undefined
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    proxy,
  })
